import React, { useState } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import {
  ListItem,
  ListItemAvatar,
  Avatar,
  Typography,
  Grid,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import PublicIcon from '@material-ui/icons/Public';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import {
  setConfirmationModalState,
  deleteAssumptionModule,
} from 'store/actions';
import { Constants } from 'utilities/constants';

const ModuleCard = ({
  classes,
  user,
  module,
  onClick,
  onEditDescription,
  setConfirmationModalState,
  deleteAssumptionModule,
}) => {
  // == CONSTANTS ======================================
  const {
    txnOppUnassignedBin,
    listItem,
    listItemAvatar,
    queueAvatar,
    blueAvatar,
    avatarText,
    listItemContent,
    portfolioNameContainer,
    textPrimary,
    textSecondary,
  } = classes;

  // == STATE ==========================================

  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);

  // == EVENT HANDLERS ================================

  const openMoreMenu = (e) => {
    setMoreMenuAnchorEl(e.currentTarget);
  };

  const closeMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  };

  const handleListItemClick = (event, task) => {
    onClick(module.moduleId);
  };

  const handleEditModuleDescription = () => {
    closeMoreMenu();
    onEditDescription(module);
  }

  const handleDeleteModule = (e) => {
    closeMoreMenu();
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => {
        closeConfirmationModal();
        deleteAssumptionModule(module.moduleId);
      },
      handleCancellation: () => {
        closeConfirmationModal();
      },
      titleText: 'Confirm',
      contentText: `Are you sure you want to delete ${module.name}?`,
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  };

  // == COMPONENTS ========================================
  return (
    <div className={txnOppUnassignedBin}>
      <ListItem
        button
        className={listItem}
        onClick={(e) => handleListItemClick(e)}
      >
        <ListItemAvatar className={listItemAvatar}>
          <Avatar className={classNames(queueAvatar, blueAvatar)}>
            <PublicIcon />
            <Typography className={avatarText} />
          </Avatar>
        </ListItemAvatar>
        <div className={listItemContent}>
          <Grid
            container
            wrap='nowrap'
            spacing={0}
            alignItems='center'
            direction='row'
            justifyContent='space-between'
          >
            <Grid item className={portfolioNameContainer}>
              <Typography className={textPrimary} component='div'>
                {module.name}
              </Typography>
            </Grid>
          </Grid>

          <Typography className={textSecondary} component='div'>
            {module.description ?? 'Description'}
          </Typography>
          <div style={{ height: 32 }}></div>
        </div>
      </ListItem>

      {user.roles.administrator && (
        <>
          <IconButton className={classes.detailButton} onClick={openMoreMenu}>
            <MoreHorizIcon />
          </IconButton>
          <Menu
            anchorEl={moreMenuAnchorEl}
            open={Boolean(moreMenuAnchorEl)}
            onClose={closeMoreMenu}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'bottom',
            }}
          >
            <MenuItem onClick={handleEditModuleDescription}>Edit Description</MenuItem>
            <MenuItem onClick={handleDeleteModule}>Delete Module</MenuItem>
          </Menu>
        </>
      )}
    </div>
  );
};

// == STYLES ================================================

const styles = (theme) => ({
  txnOppUnassignedBin: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 8px',
    position: 'relative',
  },
  textPrimary: {
    fontSize: '.85rem',
    fontWeight: '500',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  textUnsaved: {
    fontSize: '.85rem',
    fontWeight: '500',
    display: 'inline-block',
  },
  inlinePadding: {
    margin: '0px 5px 0px 0px',
  },
  textSecondary: {
    fontSize: '.75rem',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    margin: '-3px 0px 0px',
  },
  listItem: {
    padding: 0,
    borderRadius: 4,
    borderBottom: '1px solid #e4e4e4',
    backgroundColor: '#fff',
    overflow: 'hidden',
    height: 73,
  },
  listItemContent: {
    padding: '4px 8px',
    minWidth: 0,
    height: '100%',
  },
  listItemSelected: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)!important',
  },
  listItemAvatar: {
    minWidth: 'unset',
  },
  queueAvatar: {
    height: '72px',
    width: '24px',
    borderRadius: 0,
  },
  completed: {
    height: '72px',
    width: '24px',
    backgroundColor: '#31c700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatar: {
    marginRight: '0px',
    height: 68,
    width: 24,
    borderRadius: 0,
  },
  avatarText: {
    fontSize: '1.55rem',
    fontWeight: '500',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  blueAvatar: {
    backgroundColor: theme.palette.primary.main,
  },
  portfolioNameContainer: {
    overflow: 'hidden',
    display: 'flex',
  },
  detailButton: {
    padding: 4,
    position: 'absolute',
    right: 8,
    top: 20,
  },
});

const mapStateToProps = ({ appState }) => {
  return {
    user: appState.user,
  };
};
const mapDispatchToProps = {
  setConfirmationModalState,
  deleteAssumptionModule,
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(ModuleCard);
